<template>
  <div class="min-h-screen bg-white">
    <div class="max-w-4xl mx-auto px-4 py-3">
      <div class="text-center mb-2.5">
        <h1 class="font-header text-[3rem] xl:text-[3rem] font-bold text-primary mb-1.5">
          Press
        </h1>
        <div class="font-body text-[1.125rem] text-gray-700 max-w-4xl mx-auto leading-[1.75] content-page-description"
          v-html="pageDescription || 'Stay up to date with the latest news and announcements from <i>Barry & Bonna Publishing</i>.'">
        </div>
      </div>

      <div class="prose max-w-none">

        <div v-if="loading" class="flex justify-center items-center min-h-[400px]">
          <Loader size="large" />
        </div>

        <div v-else-if="pressItems.length > 0" class="space-y-[0.39rem]">
          <article v-for="item in pressItems" :key="item.id" class="bg-white p-2.5 rounded-lg border-l-4 border-accent">
            <div class="flex items-center justify-between mb-1">
              <time class="font-body text-[1.125rem] text-gray-300 leading-[1.75]">{{ formatDate(item.published_at ||
                item.created_at)
              }}</time>
              <span v-if="item.category" class="text-xs bg-green-700 text-white px-3 py-1 rounded-full font-header">{{
                item.category }}</span>
            </div>
            <h2 class="font-header text-[1.5rem] font-bold text-primary mb-1">
              {{ item.title }}
            </h2>
            <div v-if="item.image" class="mb-1.5 flex justify-center">
              <div class="w-full max-w-[800px] overflow-hidden rounded-lg bg-gray-100" :class="getImageFrameClass(item)"
                :style="getImageFrameStyle(item)">
                <img :src="item.image" :alt="item.title" class="w-full h-auto min-h-full object-cover" />
              </div>
            </div>
            <div class="press-content font-body text-[1.125rem] text-gray-900 leading-[1.75] content-page-section">
              <div v-if="shouldTruncateContent(item.content)" v-html="getTruncatedContent(item.content)"></div>
              <div v-else v-html="item.content"></div>
            </div>
            <button v-if="shouldTruncateContent(item.content)" @click="openModal(item)"
              class="text-primary hover:text-primary-dark font-header font-semibold text-[1.125rem] mt-2">
              Read more →
            </button>
          </article>
        </div>

        <div v-else class="text-center py-1">
          <p class="font-body text-[1.125rem] text-gray-600 leading-[1.75]">No press releases available at this time.
          </p>
        </div>

        <div class="mt-2.5 bg-white p-2.5 rounded-lg">
          <h2 class="font-header text-[1.5rem] xl:text-[1.5rem] font-bold text-primary mb-1.5">Media Inquiries</h2>
          <p class="font-body text-[1.125rem] text-gray-900 leading-[1.75] mb-1">
            For press inquiries, review copies, author interviews, or media partnerships, please contact our team.
          </p>
          <a href="/contact"
            class="inline-block bg-accent text-white px-8 py-3 rounded-md hover:bg-green-700 transition-colors font-header font-medium">
            Contact Team
          </a>
        </div>
      </div>
    </div>

    <!-- Read More Modal -->
    <div v-if="selectedItem" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      @click.self="closeModal">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col relative">
        <button @click="closeModal"
          class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10" aria-label="Close">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div class="bg-white px-8 py-6 overflow-y-auto">
          <div class="flex items-center justify-between mb-4">
            <time class="font-body text-[1.125rem] text-gray-400 leading-[1.75]">
              {{ formatDate(selectedItem.published_at || selectedItem.created_at) }}
            </time>
            <span v-if="selectedItem.category"
              class="text-xs bg-green-700 text-white px-3 py-1 rounded-full font-header">
              {{ selectedItem.category }}
            </span>
          </div>
          <h2 class="font-header text-[1.5rem] font-bold text-primary mb-4">
            {{ selectedItem.title }}
          </h2>
          <div v-if="selectedItem.image" class="mb-6 flex justify-center">
            <div class="w-full max-w-[800px] overflow-hidden rounded-lg bg-gray-100"
              :class="getImageFrameClass(selectedItem)" :style="getImageFrameStyle(selectedItem)">
              <img :src="selectedItem.image" :alt="selectedItem.title" class="w-full h-auto min-h-full object-cover" />
            </div>
          </div>
          <div class="press-content font-body text-[1.125rem] text-gray-900 leading-[1.75] content-page-section"
            v-html="selectedItem.content"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { publicService } from '../services/public'
import Loader from './Loader.vue'
import { useLoadingState } from '../composables/useLoadingState'
import { formatDate } from '../utils/date'
import { shouldTruncateHtml, truncateHtml, TRUNCATE_BY } from '../utils/htmlTruncation'

const pressItems = ref([])
const { loading, setLoading } = useLoadingState(true)
const pageDescription = ref('')
const selectedItem = ref(null)
const WORD_LIMIT = 100

const loadPress = async () => {
  setLoading(true)
  try {
    const data = await publicService.getPress()
    pressItems.value = Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Error loading press:', error)
    pressItems.value = []
  } finally {
    setLoading(false)
  }
}


function shouldTruncateContent(html) {
  return shouldTruncateHtml(html, WORD_LIMIT, { by: TRUNCATE_BY.WORD })
}

function getTruncatedContent(html) {
  return truncateHtml(html, WORD_LIMIT, { by: TRUNCATE_BY.WORD })
}

const openModal = (item) => {
  selectedItem.value = item
}

const closeModal = () => {
  selectedItem.value = null
}

const loadPageDescription = async () => {
  try {
    const page = await publicService.getPage('Press')
    if (page && page.description) {
      pageDescription.value = page.description
    }
  } catch (error) {
    console.error('Failed to load page description:', error)
  }
}

const getImageFrameClass = (item) => {
  const dims = item?.image_crop_dimensions
  const hasDims = dims && dims.width > 0 && dims.height > 0
  return hasDims ? '' : 'aspect-[16/9]'
}

const getImageFrameStyle = (item) => {
  const dims = item?.image_crop_dimensions
  if (!dims || !(dims.width > 0 && dims.height > 0)) return {}

  const { width, height } = dims
  const maxWidth = 800
  let displayWidth = width
  let displayHeight = height

  if (width > maxWidth) {
    const scale = maxWidth / width
    displayWidth = maxWidth
    displayHeight = height * scale
  }

  return {
    width: `${displayWidth}px`,
    height: `${displayHeight}px`,
    maxWidth: '100%'
  }
}

onMounted(() => {
  loadPress()
  loadPageDescription()
})
</script>

<style scoped>
/* Links */
:deep(a) {
  color: #2563eb;
  text-decoration: underline;
  transition: color 0.2s;
}

:deep(a):hover {
  color: #1e40af;
}

:deep(a.bg-accent),
:deep(a.bg-accent:hover) {
  color: white !important;
  text-decoration: none;
}

/* Headers */
.press-content :deep(h1) {
  font-size: 2rem;
  font-weight: bold;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  color: #2D4D31;
}

.press-content :deep(h2) {
  font-size: 1.75rem;
  font-weight: bold;
  margin-top: 1.25rem;
  margin-bottom: 0.875rem;
  color: #2D4D31;
}

.press-content :deep(h3) {
  font-size: 1.5rem;
  font-weight: bold;
  margin-top: 1rem;
  margin-bottom: 0.75rem;
  color: #2D4D31;
}

/* Font Sizes */
.press-content :deep(.ql-size-small) {
  font-size: 0.875rem;
}

.press-content :deep(.ql-size-large) {
  font-size: 1.25rem;
}

.press-content :deep(.ql-size-huge) {
  font-size: 1.5rem;
}

/* Blockquotes */
.press-content :deep(blockquote) {
  border-left: 4px solid #2D4D31;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: #5B4F47;
}

/* Code Blocks */
.press-content :deep(pre) {
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  padding: 1rem;
  overflow-x: auto;
  margin: 1rem 0;
  font-family: 'Courier New', monospace;
}

.press-content :deep(code) {
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  padding: 0.125rem 0.375rem;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
}

.press-content :deep(pre code) {
  background: none;
  border: none;
  padding: 0;
}

/* Images */
.press-content :deep(img) {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 1.5rem auto;
  border-radius: 0.5rem;
}

/* Videos */
.press-content :deep(iframe) {
  max-width: 100%;
  margin: 1.5rem auto;
  display: block;
  border-radius: 0.5rem;
}

.press-content :deep(.ql-video) {
  width: 100%;
  max-width: 640px;
  height: 360px;
  margin: 1.5rem auto;
  display: block;
}

/* Text Alignment */
.press-content :deep(.ql-align-center) {
  text-align: center;
}

.press-content :deep(.ql-align-right) {
  text-align: right;
}

.press-content :deep(.ql-align-left) {
  text-align: left;
}

.press-content :deep(.ql-align-justify) {
  text-align: justify;
}

.press-content :deep(p.ql-align-center),
.press-content :deep(div.ql-align-center) {
  text-align: center;
}

.press-content :deep(p.ql-align-right),
.press-content :deep(div.ql-align-right) {
  text-align: right;
}

.press-content :deep(p.ql-align-left),
.press-content :deep(div.ql-align-left) {
  text-align: left;
}

.press-content :deep(p.ql-align-justify),
.press-content :deep(div.ql-align-justify) {
  text-align: justify;
}

.press-content :deep([style*="text-align: center"]),
.press-content :deep([style*="text-align:center"]) {
  text-align: center !important;
}

.press-content :deep([style*="text-align: right"]),
.press-content :deep([style*="text-align:right"]) {
  text-align: right !important;
}

.press-content :deep([style*="text-align: left"]),
.press-content :deep([style*="text-align:left"]) {
  text-align: left !important;
}

.press-content :deep([style*="text-align: justify"]),
.press-content :deep([style*="text-align:justify"]) {
  text-align: justify !important;
}

/* Font Families */
.press-content :deep(.ql-font-serif) {
  font-family: Georgia, 'Times New Roman', serif;
}

.press-content :deep(.ql-font-monospace) {
  font-family: 'Courier New', monospace;
}

/* Lists */
.press-content :deep(ul) {
  list-style-type: disc;
  margin-left: 1.5em;
  margin-bottom: 0.25em;
}

.press-content :deep(ol) {
  list-style-type: decimal;
  margin-left: 1.5em;
  margin-bottom: 0.25em;
}

.press-content :deep(li) {
  margin-bottom: 0.125em;
}

/* Spacing */
.press-content :deep(p) {
  margin-bottom: 0.75rem;
}

.press-content :deep(p:last-child) {
  margin-bottom: 0;
}
</style>
